import { startTransition, useEffect, useState, type ComponentType } from "react";
import { Outlet } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
// NOTA: NO envolvemos con <HelmetProvider> aquí — vite-react-ssg ya provee
// uno en la raíz (tanto en SSR/prerender como en el cliente). Un provider
// anidado creaba un contexto propio que capturaba los <Helmet> de cada
// ruta e impedía que SSG los inyectara en el <head> del HTML prerenderizado
// (razón por la que todas las rutas mostraban el title/canonical de la home).
import { ThemeProvider } from "@/hooks/use-theme";
import { initScrollReveal } from "@/lib/scroll-reveal";
import { initPixelTracking } from "@/lib/pixel";
import { initOaiqTracking } from "@/lib/oaiq";
import CookieConsent from "@/components/cookies/CookieConsent";
import MobileContactBar from "@/components/layout/MobileContactBar";

const queryClient = new QueryClient();

const Layout = () => {
  const [DeferredOverlays, setDeferredOverlays] = useState<ComponentType | null>(null);

  useEffect(() => {
    initScrollReveal();
    initPixelTracking();
    initOaiqTracking();

    // Elimina la <meta name="description"> estática de index.html para que
    // sólo quede la gestionada por Helmet en cada ruta (evita duplicados).
    document
      .querySelectorAll('meta[name="description"]:not([data-rh="true"])')
      .forEach((el) => el.parentNode?.removeChild(el));



    let cancelled = false;
    let loaded = false;
    const events = ["pointerdown", "touchstart", "keydown", "scroll"] as const;

    const loadOverlays = () => {
      if (loaded) return;
      loaded = true;
      events.forEach((ev) => window.removeEventListener(ev, loadOverlays));
      window.clearTimeout(fallback);
      import("@/components/DeferredOverlays").then((module) => {
        if (cancelled) return;
        startTransition(() => setDeferredOverlays(() => module.default));
      });
    };

    events.forEach((ev) =>
      window.addEventListener(ev, loadOverlays, { once: true, passive: true })
    );

    // Fallback: cargar tras 6s para que los toasts funcionen aunque el
    // usuario no interactúe. Suficientemente tarde para no entrar en la
    // cadena crítica del LCP en móviles lentos.
    const fallback = window.setTimeout(loadOverlays, 6000);

    return () => {
      cancelled = true;
      window.clearTimeout(fallback);
      events.forEach((ev) => window.removeEventListener(ev, loadOverlays));
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <Outlet />
        {DeferredOverlays ? <DeferredOverlays /> : null}
        <CookieConsent />
        <MobileContactBar />
      </ThemeProvider>
    </QueryClientProvider>
  );
};



export default Layout;

